import React, { useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Building2, Phone, MapPin, Smile, LoaderCircle, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { businessFormSchema, BusinessFormValues } from "@/schemas/sellerRegistrationSchema";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { countries } from "@/utils/countries";
import { ScrollArea } from "../ui/scroll-area";


type BusinessInformationFormProps = {
  onSubmit?: (data: BusinessFormValues) => void;
  defaultValues?: Partial<BusinessFormValues>;
  loading?: boolean;
};

const DistributorRegistrationForm = ({
  onSubmit,
  defaultValues,
  loading,
}: BusinessInformationFormProps) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const form = useForm<BusinessFormValues>({
    resolver: zodResolver(businessFormSchema),
    defaultValues: {
      businessName: "",
      description: "",
      phone: "",
      suite: "",
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
      ...defaultValues,
    },
  });

  // const handleImageChange = (files: FileList | null, onChange: (file: File) => void) => {
  //   if (files && files[0]) {
  //     const selectedFile = files[0];
  //     onChange(selectedFile);

  //     // Create preview for images only
  //     const previewUrl = URL.createObjectURL(selectedFile);
  //     setImagePreview(previewUrl);
  //   }
  // };



  const handleSubmit = async (data: BusinessFormValues) => {
    // let license_path;
    // if (data.license instanceof File) {
    //   license_path = await uploadFileToBucket(data.license, "seller-license-pictures");
    // } else if (!data.license) {
    //   license_path = "";
    //   toast.error("license is required");
    //   return;
    // }

    // const formData = { ...data, license: license_path };

    onSubmit?.(data);
  };

  return (
    <div className="min-h-full bg-gray-50 flex items-center justify-center  py-8 ">
      <Card className="w-full max-w-4xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold">Become a Distributor</CardTitle>
          <CardDescription className="text-gray-600">
            Create your distributor account to start selling your products on our platform.
          </CardDescription>
        </CardHeader>

        <div className="px-6">
          <div className="flex justify-center mb-8">
            <div className="flex space-x-8">
              <div className="flex items-center space-x-2 text-black">
                <span className="text-sm font-medium">Business Information</span>
              </div>
            </div>
          </div>
        </div>

        <CardContent className="space-y-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="businessName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-900">
                      Business Name
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                        <Input placeholder="Your Business Name" className="pl-10 h-11" {...field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-900">
                      Business Description
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Textarea
                          placeholder="Tell us about your business and what you sell..."
                          className="min-h-[120px] resize-none"
                          {...field}
                        />
                        <Smile className="absolute right-3 bottom-3 h-4 w-4 text-gray-400" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-900">
                      Business Phone
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                        <Input placeholder="(555) 123-4567" className="pl-10 h-11" {...field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="street"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-900">Street</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                          <Input placeholder="456 Elm Street " className="pl-10 h-11" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="suite"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-900">Suite</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                          <Input placeholder="123 Main St" className="pl-10 h-11" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-900">City</FormLabel>
                      <FormControl>
                        <Input placeholder="City" className="h-11" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-900">State</FormLabel>
                      <FormControl>
                        <Input placeholder="State" className="h-11" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="zipCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-900">ZIP Code</FormLabel>
                      <FormControl>
                        <Input placeholder="12345" className="h-11" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-900">Country</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="h-11 w-full">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <ScrollArea className="h-44">
                            {countries.map((country) => (
                              <SelectItem key={country.value} value={country.value}>
                                {country.label}
                              </SelectItem>
                            ))}
                          </ScrollArea>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* <FormField
                control={form.control}
                name="license"
                render={({ field: { onChange, value, ref, ...rest } }) => (
                  <FormItem>
                    <FormLabel>License</FormLabel>
                    <FormControl>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 mb-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={loading}
                          >
                            Choose File
                          </Button>
                          <span className="text-sm text-gray-500">
                            {form.watch("license")
                              ? `File selected: ${(form.watch("license") as File)?.name}`
                              : "No file selected"}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 mb-2">
                          Allowed file types: jpeg, jpg, png
                        </div>
                        <Input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          ref={fileInputRef}
                          onChange={(e) => handleImageChange(e.target.files, onChange)}
                          disabled={loading}
                          className="hidden"
                        />
                        {form.watch("license") && imagePreview ? (
                          <div className="mt-2 relative">
                            <div className="border border-gray-200 rounded-md p-2 w-fit relative">
                              <img
                                src={imagePreview}
                                alt="License preview"
                                className="max-w-[200px] max-h-[200px] object-contain"
                              />
                              <X
                                onClick={handleRemoveImage}
                                className="absolute -top-2 -right-2 z-10 w-5 h-5 cursor-pointer bg-white rounded-full p-1 text-black border border-gray-300"
                              />
                            </div>
                          </div>
                        ) : (
                          <></>
                          // <div className="flex items-center justify-center w-full max-w-[200px] h-[100px] border border-dashed border-gray-300 rounded-md bg-gray-50">
                          //   <ImageIcon className="h-6 w-6 text-gray-400" />
                          // </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}

              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <Button
                  disabled={loading}
                  type="submit"
                  className="flex-1 h-12 bg-black hover:bg-gray-800"
                >
                  {loading ? (
                    <>
                      <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                      <span>Please wait...</span>
                    </>
                  ) : (
                    " Create Seller Account"
                  )}
                </Button>
              </div>
            </form>
          </Form>

          <div className="text-center pt-4 border-t">
            <p className="text-sm text-gray-600">
              Already have a seller account?{" "}
              <Link to="/login" className="text-black hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DistributorRegistrationForm;
